import axios from "axios";
import Config from "../_config/config";

export interface SubmissionForGrading {
  id: number;
  userId: number;
  userName: string | null;
  studyResourceId: number;
  resourceName: string | null;
  status: "DRAFT" | "SUBMITTED" | "IN_REVIEW" | "GRADED" | "RETURNED" | "COMPLETED";
  submittedAt: string;
  isLate: boolean;
  score: string | null;
  maxScore: string | null;
  grade: string | null;
  dueDate: string | null;
  reviewedByUserId: number | null;
  gradedAt: string | null;
}

export interface SubmissionStats {
  total: number;
  pending: number;
  inReview: number;
  graded: number;
}

export interface GetSubmissionsResponse {
  success: boolean;
  data?: {
    submissions: SubmissionForGrading[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
    stats: SubmissionStats;
  };
  error?: string;
  message?: string;
}

export interface UpdateGradeRequest {
  score: number;
  maxScore: number;
  grade: string;
  feedbackText: string;
  reviewerComments?: string;
  criteriaScores?: {
    [criterionId: string]: {
      score: number;
      feedback: string;
    };
  };
  status: "IN_REVIEW" | "GRADED";
  reviewedByUserId: number;
}

export interface UpdateGradeResponse {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
}

export interface BulkActionRequest {
  submissionIds: number[];
  action: "auto-grade" | "return" | "mark-in-review";
  reviewedByUserId: number;
}

export interface BulkActionResponse {
  success: boolean;
  data?: {
    results: Array<{
      submissionId: number;
      status: "success" | "error" | "skipped";
      message: string;
      score?: number;
    }>;
  };
  error?: string;
}

export interface StudentSubmission extends SubmissionForGrading {
  feedbackText: string | null;
  reviewerComments: string | null;
}

export interface StudentWithSubmissions {
  id: number;
  name: string | null;
  userCode: string | null;
  studentCode: string | null;
  enrollmentId: number | null;
  totalSubmissions: number;
  pendingCount: number;
  inReviewCount: number;
  gradedCount: number;
  averageScore: number | null;
  lastSubmissionDate: string | null;
  submissions: StudentSubmission[];
}

export interface GetStudentsWithSubmissionsResponse {
  success: boolean;
  data?: {
    students: StudentWithSubmissions[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
  error?: string;
  message?: string;
}

/**
 * Service for AI-Assisted Grading Studio
 */
class GradingService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/grading`;
  }

  /**
   * Get auth headers with Bearer token
   */
  private getAuthHeaders(): any {
    const headers: any = {
      "Content-Type": "application/json",
    };

    const currentUser = localStorage.getItem("currentUser");
    console.log("[GradingService] currentUser from localStorage:", currentUser);

    if (currentUser && currentUser !== "undefined") {
      try {
        const user = JSON.parse(currentUser);
        console.log("[GradingService] Parsed user:", user);
        const token = user.token;
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
          console.log("[GradingService] Added Authorization header");
        } else {
          console.warn("[GradingService] No token found in currentUser");
        }
      } catch (error) {
        console.error("[GradingService] Failed to parse currentUser from localStorage:", error);
      }
    } else {
      console.warn("[GradingService] No currentUser in localStorage");
    }

    return headers;
  }

  /**
   * Get submissions for grading with filtering
   */
  async getSubmissions(params: {
    studyResourceId?: number;
    courseId?: number;
    status?: string[];
    rubricId?: number;
    sort?: "dueDate" | "submittedAt" | "studentName";
    limit?: number;
    offset?: number;
  }): Promise<GetSubmissionsResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.studyResourceId) {
        queryParams.append("studyResourceId", params.studyResourceId.toString());
      }
      if (params.courseId) {
        queryParams.append("courseId", params.courseId.toString());
      }
      if (params.status && params.status.length > 0) {
        queryParams.append("status", params.status.join(","));
      }
      if (params.rubricId) {
        queryParams.append("rubricId", params.rubricId.toString());
      }
      if (params.sort) {
        queryParams.append("sort", params.sort);
      }
      if (params.limit) {
        queryParams.append("limit", params.limit.toString());
      }
      if (params.offset) {
        queryParams.append("offset", params.offset.toString());
      }

      const url = `${this.baseUrl}/submissions?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: this.getAuthHeaders(),
      });

      return response.data;
    } catch (error: any) {
      console.error("[GradingService] Error fetching submissions:", error);
      console.error("[GradingService] Response data:", error.response?.data);
      console.error("[GradingService] Status code:", error.response?.status);

      if (error.response?.status === 401) {
        return {
          success: false,
          error: "Authentication failed. Please log in again.",
        };
      }

      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.message || "Failed to fetch submissions",
      };
    }
  }

  /**
   * Update submission grade
   */
  async updateGrade(
    submissionId: number,
    gradeData: UpdateGradeRequest
  ): Promise<UpdateGradeResponse> {
    try {
      const url = `${this.baseUrl}/submissions/${submissionId}`;
      const response = await axios.patch(url, gradeData, {
        headers: this.getAuthHeaders(),
      });

      return response.data as unknown as UpdateGradeResponse;
    } catch (error: any) {
      console.error("[GradingService] Error updating grade:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to update grade",
      };
    }
  }

  /**
   * Perform bulk action on submissions
   */
  async bulkAction(
    actionData: BulkActionRequest
  ): Promise<BulkActionResponse> {
    try {
      const url = `${this.baseUrl}/submissions/bulk-action`;
      const response = await axios.post(url, actionData, {
        headers: this.getAuthHeaders(),
      });

      return response.data as unknown as BulkActionResponse;
    } catch (error: any) {
      console.error("[GradingService] Error in bulk action:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to perform bulk action",
      };
    }
  }

  /**
   * Get students with their submissions for student-centric view
   */
  async getStudentsWithSubmissions(params: {
    courseId?: number;
    batchId?: number;
    schoolId?: number;
    studyResourceId?: number;
    status?: string[];
    search?: string;
    sort?: "name" | "submissionCount" | "pendingCount";
    limit?: number;
    offset?: number;
  }): Promise<GetStudentsWithSubmissionsResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.courseId) {
        queryParams.append("courseId", params.courseId.toString());
      }
      if (params.batchId) {
        queryParams.append("batchId", params.batchId.toString());
      }
      if (params.schoolId) {
        queryParams.append("schoolId", params.schoolId.toString());
      }
      if (params.studyResourceId) {
        queryParams.append("studyResourceId", params.studyResourceId.toString());
      }
      if (params.status && params.status.length > 0) {
        queryParams.append("status", params.status.join(","));
      }
      if (params.search) {
        queryParams.append("search", params.search);
      }
      if (params.sort) {
        queryParams.append("sort", params.sort);
      }
      if (params.limit) {
        queryParams.append("limit", params.limit.toString());
      }
      if (params.offset) {
        queryParams.append("offset", params.offset.toString());
      }

      const url = `${this.baseUrl}/students?${queryParams.toString()}`;
      const response = await axios.get(url, {
        headers: this.getAuthHeaders(),
      });

      return response.data;
    } catch (error: any) {
      console.error("[GradingService] Error fetching students with submissions:", error);
      console.error("[GradingService] Response data:", error.response?.data);
      console.error("[GradingService] Status code:", error.response?.status);

      if (error.response?.status === 401) {
        return {
          success: false,
          error: "Authentication failed. Please log in again.",
        };
      }

      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.message || "Failed to fetch students",
      };
    }
  }

  /**
   * Get study resources linked to a lesson plan step
   */
  async getResourcesByStep(stepId: number): Promise<GetResourcesByStepResponse> {
    try {
      const url = `${Config.nodeSiteUrl}v1/study-resources/by-step/${stepId}`;
      const response = await axios.get(url, {
        headers: this.getAuthHeaders(),
      });

      return response.data;
    } catch (error: any) {
      console.error("[GradingService] Error fetching resources by step:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch resources for step",
      };
    }
  }
}

export interface StepResource {
  id: number;
  name: string | null;
  typeId: number | null;
  subTypeId: number | null;
  url: string | null;
  instructorId: number | null;
  creationDate: string | null;
  rubricId: number | null;
  typeName: string | null;
  sortOrder: number;
}

export interface GetResourcesByStepResponse {
  success: boolean;
  data?: StepResource[];
  count?: number;
  error?: string;
}


export default new GradingService();
